import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import App from './App';
import { makeTheme } from './theme';
import { makeDesktopTheme } from './desktop/theme';
import useIsDesktop from './hooks/useIsDesktop';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { InstallPromptProvider } from './context/InstallPromptContext';
import { PushPromptProvider } from './context/PushPromptContext';
import { WhatsNewProvider } from './context/WhatsNewContext';
import { UnsavedChangesProvider } from './context/UnsavedChangesContext';
import './styles/design.css';

/*
 * A data router (createBrowserRouter), not <BrowserRouter>: only a data router
 * supports `useBlocker`, which the unsaved-changes guard
 * (context/UnsavedChangesContext) needs to hold a navigation while it asks. One
 * catch-all route hands everything to <App/>, which keeps choosing the phone or
 * the desktop route tree exactly as before — those stay descendant <Routes>.
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <UnsavedChangesProvider>
        <App />
      </UnsavedChangesProvider>
    ),
  },
]);

function Themed() {
  const { dark } = useTheme();
  // Same brand tokens either way — the desktop theme only changes density
  // (36px controls instead of the phone's 46px thumb targets) and the component
  // tokens that follow from it. See src/desktop/theme.js.
  const desktop = useIsDesktop();
  return (
    <ConfigProvider
      theme={desktop ? makeDesktopTheme(dark) : makeTheme(dark)}
      // Desktop popups are anchored to the field's own container instead of
      // AntD's default of portaling to document.body. On some Chrome setups
      // (seen on a Windows/150%-scaling Lenovo) the body-relative alignment
      // computes a wildly off-screen position — the dropdown opens WITH its
      // options but lands thousands of px to the left, so it reads as "no
      // dropdown". Anchoring to the trigger's own container uses a local,
      // correct positioning context and sidesteps it — the same fix the admin
      // portal's AgentSelect already uses. Phone build keeps the default: its
      // pickers are bottom sheets (SheetSelect), not popups.
      getPopupContainer={desktop ? (node) => node?.parentElement || document.body : undefined}
    >
      <AntApp>
        {/* Above the router: `beforeinstallprompt` fires once per page load, so the
            listener has to be mounted before any route decides to render. */}
        <InstallPromptProvider>
          <AuthProvider>
            {/* Inside both: it asks only once there is a session to attach a
                subscription to, and defers to the install sheet rather than
                stacking on top of it. */}
            <PushPromptProvider>
              {/* Release notes: reads only localStorage, but sits inside the two
                  nudge providers so the sheet can yield to them on a first launch. */}
              <WhatsNewProvider>
                <RouterProvider router={router} />
              </WhatsNewProvider>
            </PushPromptProvider>
          </AuthProvider>
        </InstallPromptProvider>
      </AntApp>
    </ConfigProvider>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ThemeProvider>
      <Themed />
    </ThemeProvider>
  </StrictMode>
);

// Service-worker registration now lives in components/ReloadPrompt.jsx (vite-plugin-pwa,
// registerType 'prompt') so installed home-screen apps get a "new version" banner.
